import 'moment-timezone'

import { CampaignPhase, CampaignPostDTO, ContentKind, MediaFormat, PostStatus, SocialCampaignDTO, SocialNetwork } from 'models/socialCampaign'
import { PostDisplayStatus, campaignDayOf, dateFormatOf, dayLocaleOf, postDisplayStatus } from './campaignStatus'

import moment from 'moment'

/**
 * The schedule of a campaign (ISSUE-3623, prototype v3 `scheduleSection`, `listHtml`, `rowHtml`, `renderBar`): its
 * posts by week in the campaign's timezone, what folds once live, and what the action bar offers.
 */

/** When a post goes out, in the campaign's timezone: `YYYY-MM-DD` and `HH:mm`. */
export interface PostSlot {
  day: string
  time: string
}

export const postSlotOf = (post: Pick<CampaignPostDTO, 'publishAt'>, timezone: string): PostSlot => {
  const at = moment(post.publishAt).tz(timezone)
  return { day: at.format('YYYY-MM-DD'), time: at.format('HH:mm') }
}

/** The Monday of a day's week (a Sunday closes its week). */
export const mondayOf = (day: string) => moment.utc(day, 'YYYY-MM-DD').isoWeekday(1).format('YYYY-MM-DD')

export const addDays = (day: string, days: number) => moment.utc(day, 'YYYY-MM-DD').add(days, 'days').format('YYYY-MM-DD')

export interface ScheduleWeek {
  /** YYYY-MM-DD */
  monday: string
  posts: CampaignPostDTO[]
}

/** The posts by week of the campaign's timezone, in the order given (publication order). */
export const groupPostsByWeek = (posts: CampaignPostDTO[], timezone: string): ScheduleWeek[] => {
  const weeks: ScheduleWeek[] = []
  for (const post of posts) {
    const monday = mondayOf(postSlotOf(post, timezone).day)
    const last = weeks[weeks.length - 1]
    const week = last?.monday === monday ? last : weeks.find((item) => item.monday === monday)
    if (week) week.posts.push(post)
    else weeks.push({ monday, posts: [post] })
  }
  return weeks
}

/** How a week's heading names it: "This week", "Next week", "Last week", else "Week of" its range. */
export type WeekRelation = 'last' | 'this' | 'next' | 'other'

export const weekRelationOf = (monday: string, today: string): WeekRelation => {
  const thisMonday = mondayOf(today)
  if (monday === thisMonday) return 'this'
  if (monday === addDays(thisMonday, 7)) return 'next'
  if (monday === addDays(thisMonday, -7)) return 'last'
  return 'other'
}

const utcDate = (day: string) => new Date(`${day}T00:00:00Z`)

/**
 * A week from Monday to Sunday in the UI language ("12 – 18 Oct", "26 Oct – 1 Nov"; "12.–18. Okt."). Runs in
 * renders, so it never throws: English for a language Intl rejects, the day itself when it cannot be read.
 */
export const formatWeekRange = (monday: string, language: string) => {
  const from = utcDate(monday)
  if (Number.isNaN(from.getTime())) return monday
  const to = utcDate(addDays(monday, 6))
  const locale = dayLocaleOf(language)
  return dateFormatOf(locale, { day: 'numeric', month: 'short', timeZone: 'UTC' })
    .formatRangeToParts(from, to)
    // English ranges come unspaced ("12–18 Oct", ICU before CLDR 48) or spaced with plain or thin spaces, depending on
    // the ICU and the range: one form everywhere, thin spaces. The other languages read the same in all of them.
    .map((part) => locale === 'en-GB' && part.type === 'literal' ? part.value.replace(/\s*–\s*/, '\u2009–\u2009') : part.value)
    .join('')
}

/** A row's date column: the day of the month and the short weekday in the UI language (never throws). */
export const formatRowDate = (day: string, language: string) => {
  const date = utcDate(day)
  if (Number.isNaN(date.getTime())) return { dayOfMonth: day, weekday: '' }
  return {
    dayOfMonth: String(date.getUTCDate()),
    weekday: dateFormatOf(dayLocaleOf(language), { weekday: 'short', timeZone: 'UTC' }).format(date),
  }
}

const isGone = (status: PostDisplayStatus) => status === PostDisplayStatus.PUBLISHED || status === PostDisplayStatus.NOT_PUBLISHED

export interface ScheduleSplit {
  /** What went out, folded into one line above the list; empty unless the campaign is launched and not ended */
  folded: CampaignPostDTO[]
  /** The list: what comes next once launched, everything on a draft or an ended campaign */
  shown: CampaignPostDTO[]
  /** Of the folded posts */
  published: number
  notPublished: number
}

/**
 * Once launched the list opens on what comes next: the posts that went out, published or not, fold into one line
 * above it. A draft has nothing gone; an ended campaign shows everything, there is nothing ahead to put first.
 */
export const splitSchedule = (campaign: SocialCampaignDTO, now: Date): ScheduleSplit => {
  const folds = campaign.phase === CampaignPhase.LIVE || campaign.phase === CampaignPhase.PAUSED || campaign.phase === CampaignPhase.QUIET
  if (!folds) return { folded: [], shown: campaign.posts, published: 0, notPublished: 0 }

  const split: ScheduleSplit = { folded: [], shown: [], published: 0, notPublished: 0 }
  for (const post of campaign.posts) {
    const status = postDisplayStatus(post, campaign, now)
    if (!isGone(status)) {
      split.shown.push(post)
      continue
    }
    split.folded.push(post)
    if (status === PostDisplayStatus.PUBLISHED) split.published++
    else split.notPublished++
  }
  return split
}

/** What the floating bar (the bottom bar on a phone) offers. */
export type ActionBarState =
  /** Drafts to decide: "N of M approved", Approve all and Review all, or Launch campaign once every one is approved */
  | { kind: 'review', approved: number, drafts: number }
  /** Nothing left to decide on a launched campaign: Add a post, Generate new posts */
  | { kind: 'grow' }

/**
 * Drafts first, on a draft campaign or on a launched one after "Generate new posts" (paused included); then, once
 * launched, adding and generating. No bar on an ended campaign, nor on a draft without a draft to decide. A launched
 * campaign's draft whose time passed is not one to decide: it reads not published (`postDisplayStatus`).
 */
export const actionBarStateOf = (campaign: SocialCampaignDTO, now: Date): ActionBarState | null => {
  if (campaign.phase === CampaignPhase.ENDED) return null
  const drafts = campaign.posts.filter((post) =>
    post.status === PostStatus.DRAFT && postDisplayStatus(post, campaign, now) !== PostDisplayStatus.NOT_PUBLISHED)
  if (drafts.length) return { kind: 'review', approved: drafts.filter((post) => post.reviewed).length, drafts: drafts.length }
  return campaign.phase === CampaignPhase.DRAFT ? null : { kind: 'grow' }
}

/**
 * What launching a draft campaign (approve-all) sends: its drafts whose time is past the edit freeze, and of them those
 * still to approve. The backend skips the others ("approved too late", `SocialCampaign.scheduling`), never sent late.
 */
export const launchCountsOf = (campaign: SocialCampaignDTO, now: Date) => {
  const freezeEnds = now.getTime() + campaign.editFreezeMinutes * 60_000
  const sent = campaign.posts.filter((post) => post.status === PostStatus.DRAFT && Date.parse(post.publishAt) > freezeEnds)
  return { drafts: sent.length, toApprove: sent.filter((post) => !post.reviewed).length }
}

/** "Review all" opens the first draft still to decide, else the first draft. */
export const firstDraftToReview = (posts: CampaignPostDTO[]) =>
  posts.find((post) => post.status === PostStatus.DRAFT && !post.reviewed) ?? posts.find((post) => post.status === PostStatus.DRAFT) ?? null

/** "Generate new posts": the backend's choices (ISSUE-3984) and its default, "4 posts across 2 weeks". */
export const GENERATE_POSTS_CHOICES = [2, 3, 4, 5, 6, 8] as const
export const GENERATE_WEEKS_CHOICES = [1, 2, 3, 4] as const
export const GENERATE_DEFAULT = { posts: 4, weeks: 2 }

/**
 * The first day new posts go out: the day after the last post (any post, skipped and reserved ones included), or
 * tomorrow when that day has passed, in the campaign timezone. Keep in step with the backend's
 * `SocialCampaignService.generatePosts` (ISSUE-3984), which places the wave.
 */
export const nextGenerationStart = (campaign: SocialCampaignDTO, now: Date) => {
  const today = campaignDayOf(now.toISOString(), campaign.timezone)
  const last = campaign.posts.reduce((latest, post) => {
    const day = postSlotOf(post, campaign.timezone).day
    return day > latest ? day : latest
  }, today)
  return addDays(last, 1)
}

/** A row's square cover: its first visual. */
export type PostCover =
  /** A video shows its file's first frame with a video marker: the backend signs no thumbnail for it */
  | { kind: 'image' | 'video' | 'document', url: string | null }
  /** Reserved for an order: the kind it waits for, on a dashed tile */
  | { kind: 'waiting', url: null }
  | { kind: 'none', url: null }

export const coverOf = (post: CampaignPostDTO): PostCover => {
  if (post.status === PostStatus.WAITING_FOR_CONTENT) return { kind: 'waiting', url: null }
  const first = post.media[0]
  if (!first) return { kind: 'none', url: null }
  switch (first.reference.format) {
    case MediaFormat.IMAGE:
      return { kind: 'image', url: first.thumbnailUrl ?? first.previewUrl }
    case MediaFormat.VIDEO:
      return { kind: 'video', url: first.previewUrl }
    case MediaFormat.DOCUMENT:
      return { kind: 'document', url: first.thumbnailUrl }
  }
}

/** The deterministic story's highlight keys (ISSUE-3984) per kind of content. */
const HIGHLIGHT_KIND: Record<string, ContentKind> = {
  video: ContentKind.VIDEOS,
  'floor-plan': ContentKind.FLOOR_PLANS,
  staging: ContentKind.VIRTUAL_STAGING,
  drone: ContentKind.DRONE,
}

/**
 * The kind of content a post reserved for an order waits for. The DTO does not say it (ISSUE-3987 will): read from
 * its visuals when it has any, else from its story step (`hl:<highlight>:<cycle>[:w<wave>]`) when the deterministic
 * story wrote it; unknown otherwise (a generated highlight's key is the model's own word).
 */
export const waitingKindOf = (post: Pick<CampaignPostDTO, 'media' | 'storyKey'>): ContentKind | null => {
  const fromMedia = post.media[0]?.reference.contentKind
  if (fromMedia) return fromMedia
  const [step, highlight] = post.storyKey?.split(':') ?? []
  if (step !== 'hl' || !highlight) return null
  if (/^photos-\d+$/.test(highlight)) return ContentKind.PHOTOS
  // the key may be the model's own word: only the map's own keys, never `constructor` or `__proto__`
  return Object.hasOwn(HIGHLIGHT_KIND, highlight) ? HIGHLIGHT_KIND[highlight] : null
}

/**
 * TikTok wants its settings and consent confirmed post by post (prototype v2, Content Posting API): a post that will go
 * out on TikTok shows a dot on its mark until then. Nothing confirms it yet (ISSUE-3630), so every such post shows it.
 */
export const tiktokToConfirm = (post: Pick<CampaignPostDTO, 'networks'>, status: PostDisplayStatus) =>
  post.networks.includes(SocialNetwork.TIKTOK) && (status === PostDisplayStatus.SCHEDULED || status === PostDisplayStatus.HELD)
